import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { slaApi } from '@/shared/api/sla';

import { ticketsApi, type StatusCategory } from '@/shared/api/tickets';

/**
 * The system views of 13 §4.4.1: predefined, not editable, and each one a URL. What a view is, is a
 * set of filters the endpoint already admits - a filter the API does not support does not exist in
 * the console.
 */
// "De mi equipo" and "Resueltos hoy" stay out: /tickets filters neither actor teams nor resolvedAt.
export const TICKET_VIEWS = [
  'mine',
  'unassigned',
  'at-risk',
  'overdue',
  'pending',
  'open',
  'all',
] as const;

export type TicketView = (typeof TICKET_VIEWS)[number];

/**
 * The label key of each view: the id is the segment the view is named by, the key is camelCase as
 * every other key of `tickets.json` is. The record is exhaustive by type, so a new view cannot be
 * added without its name.
 */
export const TICKET_VIEW_LABELS: Record<TicketView, string> = {
  mine: 'mine',
  unassigned: 'unassigned',
  'at-risk': 'atRisk',
  overdue: 'overdue',
  pending: 'pending',
  open: 'open',
  all: 'all',
};

const LIVE: readonly StatusCategory[] = ['new', 'open', 'pending', 'resolved'];

export interface TicketFilters {
  ticketType?: string;
  statusCategory?: string;
  organizationId?: string;
  slaState?: string;
  serviceAreaId?: string;
  /** `AM-213`: a sede in the filter reaches its own pisos and salas too, the server's own read of it. */
  locationId?: string;
}

/**
 * `validateSearch` de `/tickets` (P028): la vista y los filtros viven en la URL, como el propio
 * comentario de este archivo ya decía (13 §4.4.1) pero el código no hacía. Sin `view`, la pantalla
 * abre en "Todos" (decisión del propietario del 2026-10-31, `AM-888`: la entrada del menú muestra todo
 * lo que la persona puede ver), así que solo se escribe en la URL cuando difiere.
 */
export interface TicketsSearch extends TicketFilters {
  view?: TicketView;
}

const TICKET_FILTER_KEYS = [
  'ticketType',
  'statusCategory',
  'organizationId',
  'slaState',
  'serviceAreaId',
  'locationId',
] as const satisfies readonly (keyof TicketFilters)[];

export function ticketsSearchSchema(search: Record<string, unknown>): TicketsSearch {
  const result: TicketsSearch = {};

  const view = search['view'];
  if (typeof view === 'string' && (TICKET_VIEWS as readonly string[]).includes(view)) {
    result.view = view as TicketView;
  }

  for (const key of TICKET_FILTER_KEYS) {
    const value = search[key];
    if (typeof value === 'string' && value.length > 0) {
      result[key] = value;
    }
  }

  return result;
}

/**
 * `validateSearch` de `/tickets/new` (`AM-302`): `replaces` es el ticket a partir del cual se abre el
 * nuevo ("Crear uno nuevo a partir de este"); sin él, la pantalla es el alta de siempre.
 */
export interface TicketNewSearch {
  replaces?: string;
}

export function ticketNewSearchSchema(search: Record<string, unknown>): TicketNewSearch {
  const replaces = search['replaces'];
  return typeof replaces === 'string' && replaces.length > 0 ? { replaces } : {};
}

/** Lo que cada vista pide al endpoint, antes de un filtro explícito que la gane (13 §4.4). */
function ticketListFilters(
  view: TicketView,
  userId: string | undefined,
  filters: TicketFilters,
): Record<string, string | undefined> {
  const base: Record<string, string | undefined> = {
    ticketType: filters.ticketType,
    organizationId: filters.organizationId,
    serviceAreaId: filters.serviceAreaId,
    locationId: filters.locationId,
  };

  if (filters.statusCategory !== undefined && filters.statusCategory !== '') {
    base['statusCategory'] = filters.statusCategory;
  } else if (view === 'pending') {
    // "Pendientes de cliente" is the `pending` category itself (`CONV-44`), not a narrower cut.
    base['statusCategory'] = 'pending';
  } else if (view !== 'all') {
    base['statusCategory'] = LIVE.join(',');
  }

  // The state the server declared, never one the browser worked out (13 §4.3.4). An explicit filter
  // wins over the view's own, the same way `statusCategory` does above.
  if (filters.slaState !== undefined && filters.slaState !== '') {
    base['slaState'] = filters.slaState;
  } else if (view === 'at-risk') {
    base['slaState'] = 'at_risk';
  } else if (view === 'overdue') {
    base['slaState'] = 'breached';
  }

  // "En riesgo de SLA" and "Vencidos" are the tenant's within the actor's reach, like "Abiertos"
  // and "Todos": neither narrows down to the tickets the actor happens to hold.
  if (view === 'mine' && userId !== undefined) {
    base['assignedAgentUserId'] = userId;
  }

  // "Sin asignar" is the server's cut (`AM-888`): the page and its total are the tickets no agent
  // holds, instead of a page of fifty narrowed in the browser under a total that counted them all.
  if (view === 'unassigned') {
    base['unassigned'] = 'true';
  }

  return base;
}

export function ticketsQueryOptions(
  view: TicketView,
  userId: string | undefined,
  filters: TicketFilters,
) {
  const base = ticketListFilters(view, userId, filters);

  return queryOptions({
    queryKey: ['tickets', 'list', view, userId, filters],
    enabled: view !== 'mine' || userId !== undefined,
    queryFn: ({ signal }) =>
      ticketsApi.list({ sort: '-updatedAt', limit: 50, countExact: true, filters: base }, signal),
  });
}

/**
 * P030: la grilla de `/tickets` traía como mucho 50 filas y nunca ofrecía ver más, aunque ya pedía
 * el total exacto. El cursor es el mismo que ya usa Auditoría y Avisos (`getNextPageParam` sobre
 * `page.nextCursor`).
 */
export function ticketsInfiniteQueryOptions(
  view: TicketView,
  userId: string | undefined,
  filters: TicketFilters,
) {
  const base = ticketListFilters(view, userId, filters);

  return infiniteQueryOptions({
    // El segmento 'infinite' no se puede quitar: esta consulta y `ticketsQueryOptions` piden lo
    // mismo, pero una guarda páginas (`{ pages, pageParams }`) y la otra una sola página
    // (`{ items, page }`). Con la misma clave compartían la entrada de caché (`filters` con claves
    // en undefined da el mismo hash que `{}`), y la Query quedaba marcada como infinita para
    // siempre: Mi trabajo, después de pasar por `/tickets`, leía las páginas como su página y
    // decía "No tenés tickets asignados."; en el orden inverso, `/tickets` caía en la pantalla de
    // error. Las invalidaciones por prefijo (`['tickets']`) siguen alcanzando a las dos.
    queryKey: ['tickets', 'list', 'infinite', view, userId, filters] as const,
    enabled: view !== 'mine' || userId !== undefined,
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) =>
      ticketsApi.list(
        {
          sort: '-updatedAt',
          limit: 50,
          countExact: true,
          filters: base,
          ...(pageParam === '' ? {} : { cursor: pageParam }),
        },
        signal,
      ),
    getNextPageParam: (last) => last.page.nextCursor ?? undefined,
  });
}

export function ticketQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id],
    queryFn: ({ signal }) => ticketsApi.get(id, signal),
  });
}

/**
 * Where this actor may move this ticket (13 §4.3.1, `AM-147` point 4). It hangs off the ticket's
 * own key and not off a key of the catalogue: the list is not the tenant's graph alone, it is that
 * graph read from the ticket's current status with the actor's own keys, so a move invalidates it
 * together with the ticket that moved.
 */
export function ticketTransitionsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'transitions'],
    queryFn: ({ signal }) => ticketsApi.transitions(id, signal),
  });
}

export function ticketCommentsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'comments'],
    queryFn: ({ signal }) => ticketsApi.listComments(id, signal),
  });
}

export function ticketWorkNotesQueryOptions(id: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'work-notes'],
    enabled,
    queryFn: ({ signal }) => ticketsApi.listWorkNotes(id, signal),
  });
}

/**
 * The files of the ticket (`AM-165`). `enabled` is the permission and not a preference: the route
 * takes `attachments.view`, so an actor without it is never made to ask for a `403` the panel would
 * then have to explain away.
 */
export function ticketAttachmentsQueryOptions(id: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'attachments'],
    enabled,
    queryFn: ({ signal }) => ticketsApi.listAttachments(id, signal),
  });
}

export function ticketAssignmentsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'assignments'],
    queryFn: ({ signal }) => ticketsApi.listAssignments(id, signal),
  });
}

export function ticketRelationshipsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['tickets', 'detail', id, 'relationships'],
    queryFn: ({ signal }) => ticketsApi.listRelationships(id, signal),
  });
}

/**
 * The promise on a ticket (12 §7.3). It is a read of its own and not part of the ticket detail,
 * because the clocks move on their own (the evaluation job breaches one without anybody touching the
 * ticket) and a stale indicator is worse than none.
 */
export function ticketSlaQueryOptions(ticketId: string) {
  return queryOptions({
    queryKey: ['tickets', ticketId, 'sla-clocks'],
    queryFn: ({ signal }) => slaApi.clocksOf(ticketId, signal),
  });
}
